/**
 * ProfilePage.js — owner-only profile view (/profile) with inline edit.
 *
 * Spec discipline: this page renders ONLY the six profile fields agreed
 * on with the client — name, type-of-org chip, website URL, profile
 * photo, bio, plus the (#posts, #locations) stats. No badges, no joined
 * date, no recent activity. If a future feature wants to surface more,
 * it has to land in its own PR with its own spec sign-off.
 *
 * Inline edit model:
 *   - Each field has a pencil button that flips it into a tiny inline
 *     form (input/select/textarea + Save/Cancel).
 *   - Save POSTs only that field via updateUserProfile.php and then
 *     refreshProfile()s; no optimistic merge — we always render the
 *     server's canonical row so manually_edited_at semantics are
 *     authoritative.
 *   - Photo is edited via a file picker on the header card; we hit
 *     uploadFiles.php (upload_target=profile_photo) and then PATCH
 *     photo_url. Remove photo PATCHes photo_url to "" (server NULLs it).
 *
 * Concurrency / multi-tab edits: tracked under TODOS.md
 * "Profile v2 — multi-tab race-condition handling for inline edits".
 * For now we accept last-write-wins, which is acceptable for owner-only
 * edits made by the same human.
 *
 * Access control: this is owner-only. We always require an authenticated
 * user (which is what getUserProfile.php enforces too). Anonymous /
 * exhibition-mode users hitting /profile see the signed-out message
 * with a route back to the map. The server is the actual authority —
 * client-side gating here is purely a UX nicety.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../authContext';
import { apiCall, ApiError } from '../api';
import { ORG_TYPE, ORG_TYPE_OPTIONS } from '../utils/constants';
import './ProfilePage.css';

const MAX_NAME = 255;
const MAX_BIO = 2000;
const MAX_URL = 500;

function labelForOrg(orgType) {
  return (
    ORG_TYPE_OPTIONS.find((o) => o.value === orgType)?.label || 'Individual'
  );
}

function validateField(field, value) {
  switch (field) {
    case 'display_name':
      if (value && value.length > MAX_NAME)
        return `Must be ${MAX_NAME} characters or fewer.`;
      return null;
    case 'bio':
      if (value && value.length > MAX_BIO)
        return `Must be ${MAX_BIO} characters or fewer.`;
      return null;
    case 'website_url':
      if (!value) return null;
      if (value.length > MAX_URL)
        return `Must be ${MAX_URL} characters or fewer.`;
      if (!/^https?:\/\//i.test(value))
        return 'Must start with http:// or https://';
      return null;
    default:
      return null;
  }
}

/**
 * Re-usable per-field inline editor. The parent is the source of truth
 * for the displayed value; this component only owns the in-flight draft.
 */
function EditableField({
  field,
  label,
  value,
  type, // 'text' | 'textarea' | 'select' | 'url'
  placeholder,
  options, // for select
  onSave,
  emptyLabel = 'Not set',
  hint,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!editing) setDraft(value || '');
  }, [value, editing]);

  const beginEdit = () => {
    setDraft(value || '');
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setDraft(value || '');
    setError(null);
  };

  const save = async () => {
    const trimmed = type === 'textarea' ? draft : draft.trim();
    const err = validateField(field, trimmed);
    if (err) {
      setError(err);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(field, trimmed);
      setEditing(false);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : (e && e.message) || 'Could not save.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="profile-field">
      <div className="profile-field__header">
        <span className="profile-field__label">{label}</span>
        {!editing && (
          <button
            type="button"
            className="profile-field__edit-btn"
            onClick={beginEdit}
            aria-label={`Edit ${label}`}
          >
            Edit
          </button>
        )}
      </div>
      {editing ? (
        <>
          <div className="profile-field__edit-row">
            {type === 'select' ? (
              <select
                className="profile-field__select"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                disabled={saving}
              >
                {options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : type === 'textarea' ? (
              <textarea
                className="profile-field__textarea"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                maxLength={MAX_BIO}
                placeholder={placeholder}
                disabled={saving}
              />
            ) : (
              <input
                className="profile-field__input"
                type={type === 'url' ? 'url' : 'text'}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                maxLength={
                  field === 'website_url'
                    ? MAX_URL
                    : field === 'display_name'
                      ? MAX_NAME
                      : undefined
                }
                placeholder={placeholder}
                disabled={saving}
              />
            )}
            <div className="profile-field__buttons">
              <button
                type="button"
                className="profile-field__btn profile-field__btn--cancel"
                onClick={cancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="profile-field__btn profile-field__btn--save"
                onClick={save}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
          {type === 'textarea' && (
            <span className="profile-field__hint">
              {(draft || '').length}/{MAX_BIO}
            </span>
          )}
          {hint && !error && (
            <span className="profile-field__hint">{hint}</span>
          )}
          {error && <span className="profile-field__error">{error}</span>}
        </>
      ) : value ? (
        <span className="profile-field__value">{value}</span>
      ) : (
        <span className="profile-field__value profile-field__value--empty">
          {emptyLabel}
        </span>
      )}
    </div>
  );
}

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, loading, profile, stats, refreshProfile } = useAuth();
  const [pageError, setPageError] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [refreshedOnce, setRefreshedOnce] = useState(false);

  // Re-fetch on mount so the page reflects any out-of-band changes
  // (e.g. wizard finished on another tab). authContext primes profile
  // on login, but a stale snapshot is still possible if the user has
  // had the tab open across a profile edit elsewhere.
  useEffect(() => {
    if (user && !refreshedOnce) {
      refreshProfile().finally(() => setRefreshedOnce(true));
    }
  }, [user, refreshProfile, refreshedOnce]);

  const handleSaveField = useCallback(
    async (field, value) => {
      setPageError(null);
      await apiCall('updateUserProfile.php', {
        method: 'POST',
        body: { [field]: value },
      });
      await refreshProfile();
    },
    [refreshProfile]
  );

  const handlePhotoChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploadingPhoto(true);
      setPageError(null);
      try {
        const formData = new FormData();
        formData.append('upload_target', 'profile_photo');
        formData.append('files[]', file);
        const resp = await apiCall('uploadFiles.php', {
          method: 'POST',
          body: formData,
        });
        const url = resp?.files?.[0]?.filepath;
        if (!url) throw new Error('Upload completed but no URL was returned.');
        await apiCall('updateUserProfile.php', {
          method: 'POST',
          body: { photo_url: url },
        });
        await refreshProfile();
      } catch (err) {
        setPageError(
          err instanceof ApiError
            ? err.message
            : (err && err.message) || 'Photo upload failed.'
        );
      } finally {
        setUploadingPhoto(false);
        if (e.target) e.target.value = '';
      }
    },
    [refreshProfile]
  );

  const handleRemovePhoto = useCallback(async () => {
    setPageError(null);
    setUploadingPhoto(true);
    try {
      await apiCall('updateUserProfile.php', {
        method: 'POST',
        body: { photo_url: '' },
      });
      await refreshProfile();
    } catch (err) {
      setPageError(
        err instanceof ApiError
          ? err.message
          : (err && err.message) || 'Could not remove photo.'
      );
    } finally {
      setUploadingPhoto(false);
    }
  }, [refreshProfile]);

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-page__loading">Loading your profile…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-page">
        <div className="profile-page__signed-out">
          You need to sign in to view your profile.{' '}
          <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>
            Back to the map
          </a>
        </div>
      </div>
    );
  }

  const displayName =
    (profile && profile.display_name) || user.displayName || user.email;
  const orgType = (profile && profile.org_type) || ORG_TYPE.INDIVIDUAL;

  return (
    <div className="profile-page">
      <button
        type="button"
        className="profile-page__back"
        onClick={() => navigate(-1)}
      >
        ← Back
      </button>

      {pageError && <div className="profile-page__error">{pageError}</div>}

      <div className="profile-card">
        <div className="profile-card__header">
          {profile?.photo_url ? (
            <img
              className="profile-card__avatar"
              src={profile.photo_url}
              alt={displayName}
            />
          ) : (
            <span className="profile-card__avatar" aria-hidden="true">
              no&nbsp;photo
            </span>
          )}
          <div className="profile-card__identity">
            <h1 className="profile-card__name">{displayName}</h1>
            <p className="profile-card__email">{user.email}</p>
            <span className="profile-card__org-chip">{labelForOrg(orgType)}</span>
          </div>
          <div className="profile-card__photo-actions">
            <label className="profile-card__photo-btn">
              {uploadingPhoto
                ? 'Working…'
                : profile?.photo_url
                  ? 'Replace photo'
                  : 'Upload photo'}
              <input
                className="profile-card__file-input"
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                disabled={uploadingPhoto}
              />
            </label>
            {profile?.photo_url && (
              <button
                type="button"
                className="profile-card__photo-btn"
                onClick={handleRemovePhoto}
                disabled={uploadingPhoto}
              >
                Remove
              </button>
            )}
          </div>
        </div>

        <div className="profile-card__stats">
          <div className="profile-card__stat">
            <div className="profile-card__stat-value">
              {stats?.posts ?? 0}
            </div>
            <div className="profile-card__stat-label">Posts</div>
          </div>
          <div className="profile-card__stat">
            <div className="profile-card__stat-value">
              {stats?.locations ?? 0}
            </div>
            <div className="profile-card__stat-label">Locations added</div>
          </div>
        </div>

        <div className="profile-card__fields">
          <EditableField
            field="display_name"
            label="Name"
            value={profile?.display_name || ''}
            type="text"
            placeholder="Your name or your organization's name"
            onSave={handleSaveField}
            emptyLabel="Not set"
          />
          {/*
            org_type is intentionally NOT user-editable post-onboarding.
            It is shown read-only as the chip in profile-card__header
            above, and can only be changed by a global admin via
            RoleManagement (which PATCHes adminUpdateUserAttrs.php).
            Server-side, updateUserProfile.php returns 403 when a
            request includes org_type and the user's onboarded_at IS
            NOT NULL — so even if a client tried to bypass this UI,
            the change would be rejected. Wizard-time write happens in
            OnboardingWizard.js while onboarded_at IS NULL.
          */}
          <EditableField
            field="website_url"
            label="Website"
            value={profile?.website_url || ''}
            type="url"
            placeholder="https://example.com"
            onSave={handleSaveField}
            emptyLabel="No website added yet"
            hint="Must start with http:// or https://"
          />
          <EditableField
            field="bio"
            label="About you"
            value={profile?.bio || ''}
            type="textarea"
            placeholder="What do you want to share about yourself, or what interests you about history and mapping?"
            onSave={handleSaveField}
            emptyLabel="No bio yet"
          />
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
